import axios from 'axios';
import { API_BASE, VAPID_PUBLIC_KEY } from '../config';
import { getToken as getAuthToken } from '../auth';
import { storeNotification } from './notificationStore';

// VAPID public key (base64url) → Uint8Array for pushManager.subscribe.
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  // Build over an explicit ArrayBuffer so the type is BufferSource-compatible
  // (a bare `new Uint8Array(len)` widens to ArrayBufferLike under TS 5.7 lib).
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof Notification !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

// Subscribe to Web Push and register the subscription with the backend. Stored
// on the same `/auth/fcm-token` endpoint the native app uses — the backend
// tells web subscriptions from native FCM tokens by shape.
export async function registerFcmToken(): Promise<void> {
  try {
    if (!VAPID_PUBLIC_KEY || !pushSupported()) return;

    const granted = await requestNotificationPermission();
    if (!granted) return;

    // The Workbox PWA service worker (/sw.js) owns push; push-sw.js is imported
    // into it (see vite.config.ts workbox.importScripts).
    const reg = await navigator.serviceWorker.ready;
    // ponytail: reuse an existing sub; the firebase SW lived under a different
    // registration, so there's no stale key to reconcile here.
    const sub = await reg.pushManager.getSubscription()
      ?? await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

    const authToken = await getAuthToken();
    if (!authToken) return;

    await axios.post(
      `${API_BASE}/auth/fcm-token`,
      { fcmToken: JSON.stringify(sub) },
      { headers: { Authorization: `Bearer ${authToken}` } },
    );
  } catch {
    // best-effort
  }
}

// Foreground: the service worker fires `push` even when a tab is focused (and
// shows the OS toast itself). push-sw.js also postMessages each push so the
// in-app inbox stays in sync — that's what this listens for.
export function setupForegroundNotificationHandler(): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const handler = (event: MessageEvent) => {
    const d: any = event.data;
    if (!d || d.kind !== 'push') return;
    storeNotification({
      title: d.title,
      body: d.body,
      type: d.type || 'BROADCAST',
      apkUrl: d.apkUrl,
      route: d.route,
    });
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}

// Resolve the in-app web path a notification should deep-link to.
// Prefers an explicit `route` (if it already looks like a path or a known
// mobile route name), otherwise maps known notification types. Returns null
// when there is no sensible destination.
export function routeForNotification(data: { type?: string; route?: string }): string | null {
  const mapRoute = (r: string): string | null => {
    if (r.startsWith('/')) return r;
    switch (r) {
      case 'Events': return '/events';
      case 'Profile': return '/profile?openHistory=1';
      default: return null;
    }
  };

  if (data.route) {
    const mapped = mapRoute(data.route.trim());
    if (mapped) return mapped;
  }

  switch (data.type) {
    case 'reminder_streak':
    case 'happy_hour_start':
    case 'happy_hour_ending':
    case 'STREAK':
    case 'BONUS':
      return '/events';
    // Payment / order activity → the player's history.
    case 'order':
    case 'topup':
    case 'withdraw':
    case 'redemption':
    case 'reminder_wallet':
    case 'TOPUP_COMPLETED':
    case 'TOPUP_COMPLETED_WINBACK':
    case 'REFERRAL_PAID':
      return '/profile?openHistory=1';
    default:
      return null;
  }
}
